import React, { useState } from "react";
import { motion } from "framer-motion";
import "./ContactSection.css";
import { MdEmail, MdLocationOn } from "react-icons/md";
import { FaPhoneAlt } from "react-icons/fa";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    message: "",
  });

  const initialFormData = {
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    message: "",
  };

  const [status, setStatus] = useState("");
  const [errors, setErrors] = useState({}); // For validation errors
  const [isSubmitting, setIsSubmitting] = useState(false); // Track submit state

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" }); // Clear error when user types
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setStatus("");
    setErrors({});
  };

  // ✅ Validation function
  const validateForm = () => {
    const newErrors = {};
    const phoneRegex = /^[0-9]{10}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = "Enter a valid 10-digit phone number";
    }

    if (formData.email.trim() && !emailRegex.test(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }

    return newErrors;
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  // Run validation before sending
  const formErrors = validateForm();
  if (Object.keys(formErrors).length > 0) {
    setErrors(formErrors);
    return;
  }

  setIsSubmitting(true);
  const datapayload = {
    "name": formData.firstName + " " + formData.lastName,
    "email": formData.email,
    "message": formData.message,
    "phone": formData.phone
  }
  try {
    const response = await fetch("https://express-brown-omega.vercel.app/api/contact", {
      method: "POST",
      body: JSON.stringify(datapayload),
      headers: {
        "Content-Type": "application/json",
      },
    });

    const result = await response.json();

    if (result.success === true) {
      alert("Your details have been submitted successfully. We will get back to you soon.");
      handleReset(); // ✅ clears fields
    } else {
      alert(result.message || "Something went wrong. Please try again.");
    }
  } catch (error) {
    alert("Failed to send. Try again later.");
  } finally {
    setIsSubmitting(false);
  }
};



  // Animation variants
  const formVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.2, duration: 0.6, ease: "easeOut" },
    }),
  };

  return (
    <div className="contact-container">
      {/* Left side */}
      <motion.div
        className="contact-info"
        initial={{ opacity: 0, x: -80 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true }}
      >
        <h2>Contact Us</h2>
        <p>We are committed to work for your success</p>

        <div className="contact-item">
          <MdEmail className="icon" />
          <span>
            <a href="mailto:info@newzealacademy.com">info@newzealacademy.com</a>
          </span>
        </div>

        <div className="contact-item">
          <MdLocationOn className="icon" />
          <span>1st Block, Rajajinagar, Bangalore - 560 010</span>
        </div>

        <div className="contact-item">
          <FaPhoneAlt className="icon" />
          <span>
            Lets Talk: <a href="tel:+919141007452">+91 63607 05213</a>
          </span>
        </div>
      </motion.div>

      {/* Right side */}
      <motion.div
        className="contact-form"
        initial={{ opacity: 0, x: 80 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true }}
      >
        <form onSubmit={handleSubmit}>
  {[
    { name: "firstName", placeholder: "First Name*", required: true },
    { name: "lastName", placeholder: "Last Name*", required: true },
    { name: "phone", placeholder: "Phone Number*", required: true },
    { name: "email", placeholder: "Email*", required: true }, // made required
  ].map((field, i) => (
    <div key={field.name} className="form-group">
      <motion.input
        type={field.name === "email" ? "email" : "text"}
        name={field.name}
        placeholder={field.placeholder}
        required={field.required} // ✅ email will also be required
        value={formData[field.name]}
        onChange={handleChange}
        custom={i}
        variants={formVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      />
      {errors[field.name] && <p className="error">{errors[field.name]}</p>}
    </div>
  ))}

  <motion.textarea
    name="message"
    placeholder="Message*"
    rows="5"
    required
    value={formData.message}
    onChange={handleChange}
    custom={4}
    variants={formVariants}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
  ></motion.textarea>

  <div className="button-group">
    <motion.button
      type="button"
      onClick={handleReset}
      whileHover={{ scale: 1.05, y: -2 }}
      whileTap={{ scale: 0.95 }}
      transition={{ type: "spring", stiffness: 200 }}
    >
      Reset
    </motion.button>
    <motion.button
      type="submit"
      whileHover={{ scale: 1.05, y: -2 }}
      whileTap={{ scale: 0.95 }}
      transition={{ type: "spring", stiffness: 200 }}
      disabled={isSubmitting} // Prevent multiple clicks
    >
      {isSubmitting ? "Submitting..." : "Submit"}
    </motion.button>
  </div>
</form>


        {status && (
          <motion.p className="status" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {status}
          </motion.p>
        )}
      </motion.div>
    </div>
  );
};

export default ContactSection;
